/**
 * Privacy-preserving masking utilities for PII in admin views
 * Balances privacy with admin's need to identify users
 */

/**
 * Mask a user's name while preserving first letter and last name
 * Example: "John Doe" → "J*** Doe"
 * @param {string} firstName
 * @param {string} lastName
 * @returns {string} masked name
 */
export function maskUserName(firstName, lastName) {
  if (!firstName || !lastName) return '';

  // Show first letter of first name + masked middle + full last name
  const first = firstName.length > 0 ? firstName.charAt(0) + '***' : '';
  const last = lastName;

  return `${first} ${last}`.trim();
}

/**
 * Mask an email address
 * Example: "jane.smith@example.com" → "jane.s***@example.com"
 * Shows enough of local part to identify, hides most of it
 * @param {string} email
 * @returns {string} masked email
 */
export function maskEmail(email) {
  if (!email || !email.includes('@')) return email;

  const [local, domain] = email.split('@');

  // Show first 2+ chars of local part, mask the rest
  const showChars = Math.max(2, Math.min(3, Math.ceil(local.length / 3)));
  const maskedLocal = local.substring(0, showChars) + '***';

  return `${maskedLocal}@${domain}`;
}

/**
 * Create a tooltip with the full unmasked info (for admin to hover and see full details)
 * @param {string} original - Original unmasked value
 * @returns {object} Title attribute for tooltip
 */
export function getMaskTooltip(original) {
  return { title: `Full: ${original}` };
}

/**
 * Mask a user's full information for display
 * @param {object} user - User object with first_name, last_name, email
 * @returns {object} Object with masked values and full values
 */
export function maskUser(user) {
  return {
    displayName: maskUserName(user.first_name, user.last_name),
    fullName: `${user.first_name} ${user.last_name}`,
    displayEmail: maskEmail(user.email),
    fullEmail: user.email,
  };
}
